import { useEffect, useState, useMemo } from "react";
import { useLocaleNavigate } from "@/hooks/useLocaleNavigate";
import { useTranslation } from "react-i18next";
import OperatorLayout from "./OperatorLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { Check, X, Search } from "lucide-react";

export default function OperatorProviders() {
  const { t } = useTranslation();
  const navigate = useLocaleNavigate();
  const [providers, setProviders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => { loadProviders(); }, [filter]);

  async function loadProviders() {
    setLoading(true);
    let query = supabase.from("provider_profiles")
      .select("*, provider_billing_accounts(*)")
      .order("created_at", { ascending: false });

    if (filter === "active") query = query.eq("is_approved", true).eq("is_suspended", false);
    if (filter === "pending") query = query.eq("is_approved", false);
    if (filter === "suspended") query = query.eq("is_suspended", true);
    if (filter === "past_due") query = query.eq("billing_status", "past_due");

    const { data } = await query;
    setProviders(data || []);
    setLoading(false);
  }

  const filteredProviders = useMemo(() => {
    if (!searchQuery.trim()) return providers;
    const q = searchQuery.toLowerCase();
    return providers.filter(p =>
      [p.company_name, p.contact_name, p.contact_email, p.city, p.region, p.postal_code, p.vat_id, p.phone]
        .filter(Boolean)
        .some(val => String(val).toLowerCase().includes(q))
    );
  }, [providers, searchQuery]);

  return (
    <OperatorLayout title={t("op.provTitle")} subtitle={t("op.provSubtitle")}>
      <div className="space-y-6">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex gap-2 flex-wrap">
            {["all", "active", "pending", "suspended", "past_due"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={cn(
                  "px-3 py-1.5 text-xs border transition-colors",
                  filter === f ? "border-accent bg-accent/5 text-foreground" : "border-border text-muted-foreground hover:text-foreground"
                )}
              >
                {f === "all" ? t("op.filterAll") : f === "active" ? t("op.filterActive") : f === "pending" ? t("op.filterPending") : f === "suspended" ? t("op.filterSuspended") : t("op.filterPastDue")}
              </button>
            ))}
          </div>
          <Button variant="commit" size="sm" onClick={() => navigate("/operator/providers/new")}>
            {t("op.createProvider")}
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t("op.searchProviders", "Suche nach Firma, Kontakt, E-Mail, Ort …")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Table */}
        <div className="border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left p-4 text-micro font-normal">{t("op.colProvider")}</th>
                <th className="text-left p-4 text-micro font-normal">{t("op.colContact")}</th>
                <th className="text-left p-4 text-micro font-normal">{t("op.colRegion")}</th>
                <th className="text-left p-4 text-micro font-normal">{t("op.colStatus")}</th>
                <th className="text-left p-4 text-micro font-normal">{t("op.colBilling")}</th>
                <th className="text-left p-4 text-micro font-normal">{t("op.colSEPA")}</th>
                <th className="text-left p-4 text-micro font-normal">{t("op.colMandate")}</th>
                <th className="text-left p-4 text-micro font-normal">{t("op.colLimit")}</th>
                <th className="text-left p-4 text-micro font-normal">{t("op.colCreated")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="p-8 text-center text-caption">{t("common.loading")}</td></tr>
              ) : filteredProviders.length === 0 ? (
                <tr><td colSpan={9} className="p-8 text-center text-caption">{searchQuery ? t("op.noSearchResults", "Keine Treffer") : t("op.noProviders")}</td></tr>
              ) : (
                filteredProviders.map((p) => (
                  <tr
                    key={p.id}
                    className="border-b border-border hover:bg-secondary/30 cursor-pointer transition-colors"
                    onClick={() => navigate(`/operator/providers/${p.id}`)}
                  >
                    <td className="p-4 font-medium">{p.company_name}</td>
                    <td className="p-4 text-caption">{p.contact_name}<br/><span className="text-micro">{p.contact_email}</span></td>
                    <td className="p-4 text-caption">{p.region || "—"}</td>
                    <td className="p-4">
                      <span className={cn(
                        "text-xs px-2 py-1 border",
                        p.is_suspended ? "border-destructive/30 bg-destructive/5 text-destructive" :
                        p.is_approved ? "border-accent/30 bg-accent/5 text-accent" :
                        "border-border bg-muted text-muted-foreground"
                      )}>
                        {p.is_suspended ? t("op.statusSuspended") : p.is_approved ? t("op.statusActive") : t("op.statusPending")}
                      </span>
                    </td>
                    <td className="p-4 text-caption">{p.billing_status || "—"}</td>
                    <td className="p-4 text-center">
                      {p.sepa_mandate_confirmed ? (
                        <Check className="w-4 h-4 text-accent inline-block" />
                      ) : (
                        <X className="w-4 h-4 text-destructive inline-block" />
                      )}
                    </td>
                    <td className="p-4 text-caption">
                      {p.provider_billing_accounts?.[0]?.mandate_status || p.provider_billing_accounts?.mandate_status || "—"}
                    </td>
                    <td className="p-4 text-caption">{p.monthly_opportunity_limit || p.weekly_lead_limit || "—"}</td>
                    <td className="p-4 text-micro">{new Date(p.created_at).toLocaleDateString("de-DE")}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </OperatorLayout>
  );
}
